import type React from "react";
import {
  Box,
  Paper,
  Grid,
  CircularProgress,
  IconButton,
  Typography,
  useTheme,
} from "@mui/material";
import { Share as ShareIcon, Print as PrintIcon } from "@mui/icons-material";
import { motion } from "framer-motion";
import type {
  IExpandedCallSheetData,
  ICallSheetPageProps,
  IUser,
} from "../../../../types/callsheet";
import { tokens } from "../../../../theme";
import { useSelector } from "react-redux";
import type { RootState } from "../../../../store/Index";
import { useParams } from "react-router-dom";
import apiService from "../../../../service/Api";
import { toast } from "react-toastify";
import type {
  CallSheetData,
  CallSheetLayoutProps,
  CrewEntry,
  CrewEntryResponse,
} from "./types/callsheet";
import CallSheetHeader from "./CallSheetHeader";
import CallSheetMainInfo from "./CallSheetMainInfo";
import CallSheetSchedule from "./CallSheetSchedule";
import CrewTable from "./CrewTable";
import CrewRecommendationsSidebar from "./CrewRecommendationsSidebar";
import ClientDetails from "./ClientDetails";
import PrintableCallSheet from "./PrintableCallsheet";
import EventDetailsPage from "./CallSheetEventDetails";
import PrintableEventDetails from "./PrintableEventDetails";
import { useCallback, useEffect, useState, useRef } from "react";
import type { TimeAllocation } from "../../hooks/useStreamData";
import ReviewModal from "./ReviewModal";
import AssignCrewModal from "./AssignCrewModal";
import jsPDF from "jspdf";
import html2canvas from "html2canvas";
import AssignmentRateDialog from "../../../crew-management/components/AssignmentRateDialog";
import MissingRoleRateDialog from "../../../crew-management/components/MissingRoleRateDialog";
import useAssignmentRateGuard from "../../../crew-management/hooks/useAssignmentRateGuard";
import {
  AssignmentRateResolution,
  createPixelPilotJobAndGig,
} from "../../../crew-management/utils/assignmentRates";
import GearPage from "./GearPage";
import PrintableGear, { PrintableGearSet } from "./PrintableGear";
// Venue-based crew recommendation modal — disabled for now; the Staffing
// sidebar covers crew recommendations from the package requirements.
// import CrewVenueModal from "./CrewVenueModal";
import CostAnalysis from "../../../cost-analysis/CostAnalysis";

export interface Role {
  id: number;
  name: string;
  description: string;
}

const CallSheetPage: React.FC<ICallSheetPageProps> = () => {
  const [assignCrewModalOpen, setAssignCrewModalOpen] = useState(false);
  const [crewVenueModalOpen, setCrewVenueModalOpen] = useState(false);
  const [reviewModalOpen, setReviewModalOpen] = useState(false);
  type CrewReview = CrewEntry & { role: string };
  const [selectedCrewForReview, setSelectedCrewForReview] =
    useState<CrewReview | null>(null);

  const [callSheetData, setCallSheetData] =
    useState<IExpandedCallSheetData | null>(null);
  const [isCallSheetLoading, setIsCallSheetLoading] = useState(true);
  const [crewMembers, setCrewMembers] = useState<CrewEntry[]>([]);
  const [roleOptions, setRoleOptions] = useState<Role[]>([]);
  const [allocatedGearSet, setAllocatedGearSet] =
    useState<PrintableGearSet | null>(null);
  const [timeData, setTimeData] = useState<CallSheetData | null>({
    call_time: "N/A",
    departure_time: "N/A",
    arrival_at_venue_time: "N/A",
    setup_from_time: "N/A",
    setup_to_time: "N/A",
    shoot_start_time: "N/A",
    shoot_end_time: "N/A",
    estimated_wrap_time: "N/A",
    back_to_base_time: "N/A",
    unpacking_time: "N/A",
    depart_time: "N/A",
  });
  const [eventDetails, setEventDetails] = useState({
    priest_name: "",
    eulogist_name: "",
    mc_name: "",
    other: "",
    program: "",
    notes: {
      travel: "",
      catering: "",
      logistics: "",
      technical: "",
      special_requirements: "",
      additional_notes: "",
    },
  });

  const [showAddButton, setShowAddButton] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [isPrinting, setIsPrinting] = useState(false);

  const theme = useTheme();
  const reduxTheme = useSelector((state: RootState) => state.theme);
  const colors = tokens(theme.palette.mode, reduxTheme);
  const { id } = useParams();

  const printRef = useRef<HTMLDivElement>(null);
  const printEventDetailsRef = useRef<HTMLDivElement>(null);
  const printGearRef = useRef<HTMLDivElement>(null);

  const {
    ensureRateForRole,
    missingRateDialogProps,
    assignmentRateDialogProps,
  } = useAssignmentRateGuard();
  const fetchRoles = async () => {
    try {
      const pageSize = 100;
      let page = 1;
      let allRoles: Role[] = [];
      let hasMore = true;

      while (hasMore) {
        const res = await apiService.get<{
          count: number;
          next: string | null;
          results: Role[];
        }>("/api/crews/roles/", { page, page_size: pageSize });

        allRoles = [...allRoles, ...(res.data.results ?? [])];
        hasMore = res.data.next != null;
        page += 1;
      }

      setRoleOptions(allRoles);
    } catch (err) {
      console.error("Failed to load roles", err);
    }
  };

  useEffect(() => {
    fetchRoles();
  }, []);

  // Fetches crew assignments + time allocation for the current stream.
  // The two requests are independent of each other, so they run in parallel.
  const fetchCrewMembers = useCallback(async () => {
    try {
      const [res, timeResponse] = await Promise.all([
        apiService.get<CrewEntryResponse>(
          `/api/call-sheets/callsheet/?stream=${id}`,
        ),
        apiService.get<TimeAllocation>(
          `/api/streams/stream/${id}/time-algorithim/`,
        ),
      ]);

      const timeJson = timeResponse.data;
      setTimeData({
        call_time: timeJson.call_time || "N/A",
        departure_time: timeJson.departure_time || "N/A",
        arrival_at_venue_time: timeJson.arrival_at_venue_time || "N/A",
        setup_from_time: timeJson.setup_from_time || "N/A",
        setup_to_time: timeJson.setup_to_time || "N/A",
        shoot_start_time: timeJson.shoot_start_time || "N/A",
        shoot_end_time: timeJson.shoot_end_time || "N/A",
        estimated_wrap_time: timeJson.estimated_wrap_time || "N/A",
        back_to_base_time: timeJson.back_to_base_time || "N/A",
        unpacking_time: timeJson.unpacking_time || "N/A",
        depart_time: timeJson.depart_time || "N/A",
      });

      const assignedCrew = (res.data.results ?? []) as CrewEntry[];
      setCrewMembers(assignedCrew);
      setCrewVenueModalOpen(assignedCrew.length === 0);
    } catch (error) {
      console.error("Error fetching crew members:", error);
      setCrewMembers([]);
      setCrewVenueModalOpen(false);
    }
  }, [id]);

  // Fetches the call sheet itself, then the crew data that depends on it.
  // `ignore` guards against a stale response overwriting fresher state if
  // `id` changes or the component unmounts before the request resolves.
  useEffect(() => {
    if (!id) return;

    let ignore = false;

    const fetchCallSheet = async () => {
      setIsCallSheetLoading(true);
      try {
        const response = await apiService.get<IExpandedCallSheetData>(
          `/api/streams/stream/${id}/`,
        );
        if (ignore) return;

        toast.success("Callsheet data fetched successfully.");
        setCallSheetData(response.data);
        await fetchCrewMembers();
      } catch (error) {
        if (ignore) return;
        console.error("Failed to fetch call sheet", error);
        toast.error(`Failed to fetch call sheet: ${error}`);
        setCallSheetData(null);
      } finally {
        if (!ignore) setIsCallSheetLoading(false);
      }
    };

    fetchCallSheet();

    return () => {
      ignore = true;
    };
  }, [fetchCrewMembers, id]);

  const handleGearSetAllocated = useCallback(
    (gearSet: PrintableGearSet | null) => {
      setAllocatedGearSet(gearSet);
    },
    [],
  );

  const handlePrintClick = async () => {
    if (!callSheetData || !printRef.current || !printEventDetailsRef.current) {
      toast.error("No data to download");
      return;
    }

    setIsPrinting(true);

    try {
      const pdf = new jsPDF({
        orientation: "p",
        unit: "mm",
        format: "a4",
        compress: true,
      });

      const pdfWidth = pdf.internal.pageSize.getWidth();

      const captureOptions = {
        scale: 2,
        useCORS: true,
        backgroundColor: "#ffffff",
        width: 794,
        windowWidth: 794,
      };

      // Page 1: Callsheet
      const canvas1 = await html2canvas(printRef.current, captureOptions);
      const imgData1 = canvas1.toDataURL("image/jpeg", 0.85);
      const page1Height = (canvas1.height * pdfWidth) / canvas1.width;
      pdf.addImage(imgData1, "JPEG", 0, 0, pdfWidth, page1Height);

      // Page 2: Gear
      if (printGearRef.current) {
        pdf.addPage();
        const canvas2 = await html2canvas(printGearRef.current, captureOptions);
        const imgData2 = canvas2.toDataURL("image/jpeg", 0.85);
        const page2Height = (canvas2.height * pdfWidth) / canvas2.width;
        pdf.addImage(imgData2, "JPEG", 0, 0, pdfWidth, page2Height);
      }

      // Page 3: Notes
      pdf.addPage();
      const canvas3 = await html2canvas(
        printEventDetailsRef.current,
        captureOptions,
      );
      const imgData3 = canvas3.toDataURL("image/jpeg", 0.85);
      const page3Height = (canvas3.height * pdfWidth) / canvas3.width;
      pdf.addImage(imgData3, "JPEG", 0, 0, pdfWidth, page3Height);

      pdf.save(
        `Call-Sheet-${callSheetData?.booking?.deceased?.[0]?.surname || "export"}.pdf`,
      );
    } catch (error) {
      console.error(error);
      toast.error("Failed to generate PDF");
    } finally {
      setIsPrinting(false);
    }
  };

  const handleShare = async () => {
    if (navigator.share && callSheetData?.website_stream_link) {
      try {
        await navigator.share({
          title: `${callSheetData?.booking?.deceased[0].first_name} ${callSheetData?.booking?.deceased[0].surname} - Memorial Service`,
          url: callSheetData.website_stream_link,
        });
      } catch (error) {
        console.error("Failed to share call sheet", error);
      }
    }
  };

  const handleAddReview = (crewMember: CrewEntry) => {
    setSelectedCrewForReview({
      ...crewMember,
      role: crewMember.assignment?.[0]?.role || "CREW MEMBER",
    });
    setReviewModalOpen(true);
  };

  const completeAddCrew = async (
    user: IUser,
    roleIds: number[],
    resolvedRate: AssignmentRateResolution,
  ) => {
    const stream_id = Number(id);
    const crew_member_id = user.id;

    interface AssignmentResponseProps {
      crew_member: { full_name: string };
      id: number;
    }

    const assignment = roleIds.map((roleId) => ({
      role: roleOptions.find((rl) => rl.id === roleId)?.name ?? "CREW MEMBER",
      notes: "",
    }));

    try {
      const alreadyAssigned = crewMembers.some((entry) => {
        const matchesCrew = entry.crew_member.id === crew_member_id;
        const matchesRole = assignment.some((newAssignment) => {
          const newRole = newAssignment.role.trim().toLowerCase();
          return (
            entry.roles?.some(
              (role) => role.name?.trim().toLowerCase() === newRole,
            ) ||
            entry.assignment?.some(
              (existingAssignment) =>
                existingAssignment.role?.trim().toLowerCase() === newRole,
            )
          );
        });
        return matchesCrew && matchesRole;
      });

      if (alreadyAssigned) {
        toast.error(
          "This crew member is already assigned to one of these roles on this callsheet.",
        );
        return;
      }

      const asgn = await apiService.post<AssignmentResponseProps>(
        "/api/call-sheets/callsheet/",
        {
          stream_id,
          crew_member_id,
          roles_ids: roleIds,
          assignment,
        },
      );

      await createPixelPilotJobAndGig({
        amount: resolvedRate.amount,
        assignedTo: user.user?.id ?? null,
        callsheetId: asgn.data.id,
        crewMemberId: crew_member_id,
        currency: resolvedRate.currency,
        description: callSheetData?.title,
        stream: {
          id: stream_id,
          title: callSheetData?.title ?? "",
          date: String(callSheetData?.date ?? ""),
          start: callSheetData?.start ?? "",
          end: callSheetData?.end ?? "",
        },
      });

      toast.success(`${user.user.first_name} added successfully!`);
      await fetchCrewMembers();
    } catch (error: unknown) {
      console.error(`Error adding ${user.user.first_name}`, error);
      let errorMessage = `Failed to add ${user.user.first_name}.`;
      const errorResponse = (
        error as {
          response?: {
            data?: {
              detail?: string;
              non_field_errors?: string[];
              [key: string]: unknown;
            };
          };
        }
      )?.response;
      if (errorResponse?.data) {
        if (typeof errorResponse.data === "string") {
          errorMessage = errorResponse.data;
        } else if (errorResponse.data.detail) {
          errorMessage = errorResponse.data.detail;
        } else if (errorResponse.data.non_field_errors) {
          errorMessage = errorResponse.data.non_field_errors.join(", ");
        } else if (Object.keys(errorResponse.data).length > 0) {
          errorMessage = Object.entries(errorResponse.data)
            .map(
              ([key, value]) =>
                `${key}: ${Array.isArray(value) ? value.join(", ") : value}`,
            )
            .join("; ");
        }
      }
      toast.error(errorMessage);
      throw error;
    }
  };

  const handleAddCrew = async (user: IUser, roleIds: number[]) => {
    if (roleIds.length === 0) {
      toast.error("Please select a role before assigning crew.");
      return false;
    }

    const primaryRole = roleOptions.find((role) => role.id === roleIds[0]);

    if (!primaryRole?.id || !primaryRole?.name) {
      toast.error("Please select a valid role before assigning crew.");
      return false;
    }

    try {
      const hasRate = await ensureRateForRole(
        {
          id: primaryRole.id,
          name: primaryRole.name,
          description: primaryRole.description,
        },
        async (resolvedRate) => {
          await completeAddCrew(user, roleIds, resolvedRate);
        },
        {
          effectiveDate: String(callSheetData?.date ?? ""),
        },
      );

      return hasRate;
    } catch (error) {
      console.error("Failed to validate the selected role rate:", error);
      toast.error(
        "Failed to validate the selected role rate. Please try again.",
      );
      return false;
    }
  };

  const handleDeleteCrew = async (entryId: number) => {
    try {
      await apiService.delete(`/api/call-sheets/callsheet/${entryId}/`);
      toast.success("Crew member removed successfully!");
      await fetchCrewMembers();
    } catch (error: unknown) {
      console.error("Error removing crew member:", error);
      let errorMessage = "Failed to remove crew member.";
      const errorResponse = (
        error as { response?: { data?: { detail?: string } } }
      )?.response;
      if (errorResponse?.data?.detail) {
        errorMessage = errorResponse.data.detail;
      }
      toast.error(errorMessage);
    }
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.1, delayChildren: 0.2 },
    },
  };

  if (isCallSheetLoading) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          padding: "4rem 0",
        }}
      >
        <CircularProgress />
      </motion.div>
    );
  }

  if (!callSheetData) {
    return (
      <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }}>
        <Typography>Booking not found.</Typography>
      </motion.div>
    );
  }

  // Venue-based crew recommendation modal is disabled for now — see the
  // commented <CrewVenueModal /> block below. The derived venue/shoot values it
  // needed are parked here until/unless it is re-enabled.
  //
  // const streamServiceVenue = callSheetData.service?.venue;
  // const bookingServiceVenue = callSheetData.booking?.services?.find(
  //   (service) => service.venue?.id,
  // )?.venue;
  // const bookingEventVenue = callSheetData.booking?.events?.find(
  //   (event) => event.venue?.id,
  // )?.venue;
  // const crewVenue = streamServiceVenue?.id
  //   ? streamServiceVenue
  //   : bookingServiceVenue?.id
  //     ? bookingServiceVenue
  //     : bookingEventVenue?.id
  //       ? bookingEventVenue
  //       : null;
  // const crewVenueId = crewVenue?.id;
  // const crewVenueShoot = {
  //   productionTitle:
  //     callSheetData.title || callSheetData.booking?.title || "N/A",
  //   location:
  //     [
  //       crewVenue?.name,
  //       crewVenue?.address_one || callSheetData.address_one,
  //       crewVenue?.address_two || callSheetData.address_two,
  //       crewVenue?.suburb || callSheetData.suburb,
  //       crewVenue?.city || callSheetData.city,
  //     ]
  //       .filter(Boolean)
  //       .join(", ") || "N/A",
  //   startTime: callSheetData.start || timeData?.shoot_start_time || "N/A",
  //   endTime: callSheetData.end || timeData?.shoot_end_time || "N/A",
  // };
  return (
    <>
      {/* Disabled for now — recommendations live in the Staffing sidebar.
      {crewVenueId && (
        <CrewVenueModal
          open={crewVenueModalOpen}
          onClose={() => setCrewVenueModalOpen(false)}
          streamId={Number(id)}
          venueId={crewVenueId}
          availableRoles={roleOptions}
          assignedCrewIds={assignedCrewIds}
          shoot={crewVenueShoot}
          onAssigned={fetchCrewMembers}
        />
      )} */}

      {/* Hidden printable components */}
      <div
        style={{
          position: "absolute",
          left: "-9999px",
          top: "-9999px",
          width: "794px",
        }}
      >
        <PrintableCallSheet
          ref={printRef}
          callSheetData={callSheetData}
          crewMembers={crewMembers}
          timeData={timeData}
          reduxTheme={reduxTheme}
          specialRequest={
            callSheetData?.booking?.services[0]?.special_request || "N/A"
          }
        />
        <PrintableGear ref={printGearRef} gearSet={allocatedGearSet} />
        <PrintableEventDetails
          ref={printEventDetailsRef}
          callSheetData={callSheetData}
          eventDetails={eventDetails}
          reduxTheme={reduxTheme}
        />
      </div>

      <Box
        sx={{
          display: "flex",
          gap: 3,
          justifyContent: "center",
          alignItems: "flex-start",
        }}
      >
      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="visible"
        style={{
          color: colors.primary[400],
          width: "210mm",
          flexShrink: 0,
          minHeight: "297mm",
          backgroundColor: "#ffffff",
          borderRadius: "12px",
          overflow: "hidden",
          padding: "16px 32px 32px",
        }}
      >
        {/* Header: page tabs + actions */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mt: 2,
            mb: 5,
          }}
        >
          <Box
            sx={{
              display: "flex",
              gap: 0.5,
              backgroundColor: colors.primary[800],
              padding: "6px",
              borderRadius: "12px",
            }}
          >
            {[
              { id: 1, label: "Callsheet" },
              { id: 2, label: "Gear" },
              { id: 3, label: "Notes" },
              { id: 4, label: "Cost Analysis" },
            ].map((page) => (
              <button
                key={page.id}
                onClick={() => setCurrentPage(page.id)}
                style={{
                  padding: "10px 24px",
                  backgroundColor:
                    currentPage === page.id
                      ? colors.primary[400]
                      : "transparent",
                  color:
                    currentPage === page.id
                      ? colors.primary[900]
                      : colors.primary[100],
                  border: "none",
                  borderRadius: "10px",
                  cursor: "pointer",
                  fontSize: "14px",
                  fontWeight: 600,
                  transition: "background-color 0.2s ease, color 0.2s ease",
                  minWidth: "100px",
                }}
              >
                {page.label}
              </button>
            ))}
          </Box>

          <Box sx={{ display: "flex", gap: 1.5 }}>
            <IconButton
              onClick={handleShare}
              sx={{
                backgroundColor: colors.primary[800],
                color: colors.primary[100],
                width: 44,
                height: 44,
              }}
            >
              <ShareIcon fontSize="small" />
            </IconButton>

            <IconButton
              onClick={handlePrintClick}
              disabled={isPrinting}
              sx={{
                backgroundColor: colors.primary[800],
                color: colors.primary[100],
                width: 44,
                height: 44,
              }}
            >
              {isPrinting ? (
                <CircularProgress size={20} color="inherit" />
              ) : (
                <PrintIcon fontSize="small" />
              )}
            </IconButton>
          </Box>
        </Box>

        {/* Page content — plain divs, no per-tab enter/exit animation.
            This avoids framer-motion variant-propagation edge cases that
            were leaving content mounted but stuck at opacity: 0 after
            switching tabs and back. */}
        {currentPage === 1 && (
          <div>
            <Paper
              elevation={0}
              sx={{ borderRadius: 1, overflow: "hidden", maxWidth: 1200 }}
            >
              <Grid container spacing={2}>
                <Grid
                  size={{ xs: 12, md: 4 }}
                  sx={{
                    borderRight: { md: `1px solid ${colors.grey[500]}` },
                  }}
                >
                  <CallSheetHeader
                    callSheetData={callSheetData ?? {}}
                    crewMembers={crewMembers ?? []}
                  />
                </Grid>
                <Grid
                  size={{ xs: 12, md: 4 }}
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    borderRight: { md: `1px solid ${colors.grey[500]}` },
                  }}
                >
                  <CallSheetMainInfo
                    callSheetData={callSheetData}
                    reduxTheme={reduxTheme}
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 4 }}>
                  <CallSheetSchedule
                    callSheetData={callSheetData ?? {}}
                    timeData={timeData}
                    reduxTheme={reduxTheme}
                  />
                </Grid>
              </Grid>
            </Paper>

            <CrewTable
              crewMembers={crewMembers}
              colors={colors}
              reduxTheme={reduxTheme}
              showAddButton={showAddButton}
              onAddCrewClick={() => setAssignCrewModalOpen(true)}
              onMouseEnter={() => setShowAddButton(true)}
              onMouseLeave={() => setShowAddButton(false)}
              onDeleteCrew={handleDeleteCrew}
              onAddReview={handleAddReview}
            />

            <ClientDetails
              callSheetData={callSheetData}
              reduxTheme={reduxTheme}
              specialRequest={
                callSheetData?.booking?.services?.[0]?.special_request || "N/A"
              }
            />
          </div>
        )}

        {currentPage === 2 && (
          <div>
            <GearPage onGearSetAllocated={handleGearSetAllocated} />
          </div>
        )}

        {currentPage === 3 && (
          <div>
            <EventDetailsPage
              callSheetData={callSheetData}
              reduxTheme={reduxTheme}
              colors={colors}
              eventDetails={eventDetails}
              setEventDetails={setEventDetails}
            />
          </div>
        )}

        {currentPage === 4 && (
          <div>
            <CostAnalysis id={id} />
          </div>
        )}

        <AssignCrewModal
          open={assignCrewModalOpen}
          onClose={() => setAssignCrewModalOpen(false)}
          onAssign={handleAddCrew}
          onSuccessClose={fetchCrewMembers}
          availableRoles={roleOptions}
          theme={reduxTheme}
          colors={colors}
          maxAssignments={8}
          currentAssignments={crewMembers.length}
        />
        <ReviewModal
          open={reviewModalOpen}
          onClose={() => setReviewModalOpen(false)}
          crewMember={selectedCrewForReview}
          theme={reduxTheme}
          colors={colors}
          streamId={Number(id)}
          onReviewSubmitted={fetchCrewMembers}
        />
        <MissingRoleRateDialog {...missingRateDialogProps} />
        <AssignmentRateDialog {...assignmentRateDialogProps} />
      </motion.div>

      {currentPage === 1 && (
        <Box
          sx={{
            display: { xs: "none", lg: "block" },
            width: 340,
            flexShrink: 0,
            position: "sticky",
            top: 20,
            alignSelf: "flex-start",
          }}
        >
          <CrewRecommendationsSidebar
            streamId={Number(id)}
            refreshKey={crewMembers.length}
            onAssign={handleAddCrew}
          />
        </Box>
      )}
      </Box>
    </>
  );
};

const CallSheetLayout: React.FC<CallSheetLayoutProps> = ({
  booking,
  booking_type,
  sx = {},
}) => {
  return (
    <Box
      sx={{
        width: "100%",
        // Wide enough for the A4 call sheet plus the staffing sidebar on lg+.
        // The call sheet keeps its own paper background; this wrapper only
        // centres it and leaves room for the sidebar beside it.
        maxWidth: { xs: "900px", lg: "1200px" },
        margin: "0 auto",
        backgroundColor: "transparent",
        boxShadow: 0,
        overflow: "visible",
        ...sx,
      }}
    >
      <CallSheetPage booking={booking} booking_type={booking_type} />
    </Box>
  );
};

export default CallSheetLayout;
